// src/pages/finance-portal/aca-compliance/ACACompliance.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, Skeleton, CircularProgress, MenuItem,
} from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

const inputSx = {
    '& .MuiOutlinedInput-root': { fontFamily, borderRadius: '10px' },
    '& .MuiInputLabel-root': { fontFamily },
};

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

export default function ACACompliance() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const currentYear = new Date().getFullYear();
    const [taxYear, setTaxYear] = useState(currentYear);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // ACA Status
    const [aleStatus, setAleStatus] = useState(false);
    const [averageFtes, setAverageFtes] = useState(0);
    const [threshold] = useState(50);
    const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);

    // 1095-C generation
    const [generating, setGenerating] = useState(false);
    const [formCount, setFormCount] = useState(null);
    const [filingStatus, setFilingStatus] = useState(null);

    const fetchACAStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.post('/api/finance/payroll/compliance/aca-status', {
                tax_year: taxYear,
            });
            if (res.data.success) {
                const data = res.data.data || {};
                setAleStatus(data.is_ale || false);
                setAverageFtes(data.average_ftes || 0);
                setMonthlyBreakdown(data.monthly_breakdown || []);
                setFormCount(data.forms_generated || null);
                setFilingStatus(data.filing_status || null);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load ACA status');
        } finally {
            setIsLoading(false);
        }
    }, [taxYear]);

    useEffect(() => { fetchACAStatus(); }, [fetchACAStatus]);

    const handleGenerate1095C = async () => {
        setGenerating(true);
        try {
            const res = await axiosInstance.post('/api/finance/payroll/compliance/1095c', {
                tax_year: taxYear,
            });
            if (res.data.success) {
                const data = res.data.data || {};
                setFormCount(data.forms_generated || 0);
                setFilingStatus(data.filing_status || 'GENERATED');
                enqueueSnackbar(`${data.forms_generated || 0} 1095-C forms generated`, { variant: 'success' });
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to generate 1095-C forms';
            enqueueSnackbar(msg, { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    const yearOptions = [];
    for (let y = currentYear; y >= currentYear - 5; y--) {
        yearOptions.push(y);
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <HealthAndSafetyIcon sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 32 }} />
                        <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.75rem', color: '#fff' }}>
                            ACA Compliance
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.95rem' }}>
                        Affordable Care Act tracking, ALE determination, and 1095-C filing
                    </Typography>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, fontFamily }}>{error}</Alert>}

                {/* Tax Year Selector */}
                <Box sx={{ mb: 3 }}>
                    <TextField
                        select
                        label="Tax Year"
                        value={taxYear}
                        onChange={(e) => setTaxYear(parseInt(e.target.value, 10))}
                        size="small"
                        sx={{ ...inputSx, minWidth: 150 }}
                    >
                        {yearOptions.map((y) => (
                            <MenuItem key={y} value={y}>{y}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                {/* ALE Status Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={0} sx={{
                            p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', gap: 2,
                        }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: '12px',
                                bgcolor: aleStatus ? '#fef2f2' : '#f0fdf4',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {aleStatus
                                    ? <CheckCircleIcon sx={{ color: '#dc2626' }} />
                                    : <CancelIcon sx={{ color: '#16a34a' }} />}
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                    ALE Status
                                </Typography>
                                <Typography sx={{
                                    fontFamily, fontSize: '1.5rem', fontWeight: 800,
                                    color: aleStatus ? '#dc2626' : '#16a34a',
                                }}>
                                    {isLoading ? <Skeleton width={40} /> : aleStatus ? 'YES' : 'NO'}
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={0} sx={{
                            p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', gap: 2,
                        }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: '12px', bgcolor: '#eff6ff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#1d4ed8',
                            }}>
                                <HealthAndSafetyIcon />
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                    Average FTEs
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: '#1d4ed8' }}>
                                    {isLoading ? <Skeleton width={40} /> : averageFtes.toFixed(1)}
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={0} sx={{
                            p: 2.5, borderRadius: '16px', border: '1px solid #e2e8f0',
                            display: 'flex', alignItems: 'center', gap: 2,
                        }}>
                            <Box sx={{
                                width: 48, height: 48, borderRadius: '12px', bgcolor: '#f5f3ff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: '#7c3aed',
                            }}>
                                <HealthAndSafetyIcon />
                            </Box>
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b', fontWeight: 600 }}>
                                    Threshold
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: '#7c3aed' }}>
                                    {threshold}
                                </Typography>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* 1095-C Generation */}
                <Paper elevation={0} sx={{
                    p: 3, borderRadius: '16px', border: '1px solid #e2e8f0', mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                1095-C Forms
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                Generate and file 1095-C forms for applicable large employers
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            {formCount !== null && (
                                <Chip
                                    label={`${formCount} forms generated`}
                                    sx={{ fontFamily, fontWeight: 600, bgcolor: '#dcfce7', color: '#166534' }}
                                />
                            )}
                            {filingStatus && (
                                <Chip
                                    label={filingStatus}
                                    size="small"
                                    sx={{
                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                        bgcolor: filingStatus === 'FILED' ? '#dcfce7' : '#dbeafe',
                                        color: filingStatus === 'FILED' ? '#166534' : '#1e40af',
                                    }}
                                />
                            )}
                            <Button
                                variant="contained"
                                startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
                                onClick={handleGenerate1095C}
                                disabled={generating}
                                sx={{
                                    fontFamily, fontWeight: 700, borderRadius: '10px',
                                    bgcolor: ACCENT, textTransform: 'none', px: 3,
                                    '&:hover': { bgcolor: '#16a34a' },
                                }}
                            >
                                {generating ? 'Generating...' : 'Generate 1095-C Forms'}
                            </Button>
                        </Box>
                    </Box>
                </Paper>

                {/* Monthly FTE Breakdown Table */}
                <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2.5, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                            Monthly FTE Breakdown
                        </Typography>
                    </Box>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}>Month</TableCell>
                                    <TableCell sx={headCellSx} align="right">Full-Time FTEs</TableCell>
                                    <TableCell sx={headCellSx} align="right">Part-Time FTEs</TableCell>
                                    <TableCell sx={headCellSx} align="right">Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(12)].map((_, i) => (
                                        <TableRow key={i}>
                                            {[...Array(4)].map((_, j) => (
                                                <TableCell key={j} sx={bodyCellSx}><Skeleton /></TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : monthlyBreakdown.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ ...bodyCellSx, textAlign: 'center', py: 4 }}>
                                            No monthly data available for {taxYear}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    monthlyBreakdown.map((row, idx) => (
                                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={bodyCellSx}>
                                                {row.month_name || MONTHS[row.month - 1] || `Month ${row.month}`}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {(row.full_time_ftes ?? row.full_time ?? 0).toFixed(1)}
                                            </TableCell>
                                            <TableCell sx={bodyCellSx} align="right">
                                                {(row.part_time_ftes ?? row.part_time ?? 0).toFixed(1)}
                                            </TableCell>
                                            <TableCell sx={{ ...bodyCellSx, fontWeight: 700 }} align="right">
                                                {(row.total_ftes ?? row.total ?? 0).toFixed(1)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
}
